import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

export function OrbitNavigation({ xyzAPI }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [currentZoneIndex, setCurrentZoneIndex] = useState(0)
  const [waypoints, setWaypoints] = useState([])
  const menuRef = useRef(null)

  // Load waypoints when xyzAPI is available
  useEffect(() => {
    if (!xyzAPI) return
    const sceneManager = xyzAPI.getSceneManager()
    if (sceneManager) {
      const wp = sceneManager.waypoints || []
      setWaypoints(wp)
    }
  }, [xyzAPI])

  // Navigation functions
  const goNext = () => {
    if (!xyzAPI || waypoints.length === 0) return
    const nextIndex = (currentZoneIndex + 1) % waypoints.length
    xyzAPI.goToSceneZoneByIndex(nextIndex)
    setCurrentZoneIndex(nextIndex)
  }

  const goPrev = () => {
    if (!xyzAPI || waypoints.length === 0) return
    const prevIndex = (currentZoneIndex - 1 + waypoints.length) % waypoints.length
    xyzAPI.goToSceneZoneByIndex(prevIndex)
    setCurrentZoneIndex(prevIndex)
  }

  const goTo = (index) => {
    if (!xyzAPI) return
    xyzAPI.goToSceneZoneByIndex(index)
    setCurrentZoneIndex(index)
    setIsMenuOpen(false)
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
      if (e.key === 'Escape') setIsMenuOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentZoneIndex, waypoints, xyzAPI])

  // Early return AFTER all hooks
  if (!xyzAPI || waypoints.length === 0) return null

  // Menu styles with animation
  // Menu styles and overlay styles replaced with Tailwind classes

  return (
    <>
      {/* Arrows */}
      <div 
        className="absolute top-1/2 left-5 -translate-y-1/2 cursor-pointer text-white text-5xl select-none z-[100] drop-shadow-md transition-all duration-200 opacity-80 hover:opacity-100 hover:scale-110"
        onClick={goPrev}
      >
        ‹
      </div>
      <div 
        className="absolute top-1/2 right-5 -translate-y-1/2 cursor-pointer text-white text-5xl select-none z-[100] drop-shadow-md transition-all duration-200 opacity-80 hover:opacity-100 hover:scale-110"
        onClick={goNext}
      >
        ›
      </div>

      {/* Hamburger Button */}
      <div 
        className={`absolute top-5 right-5 cursor-pointer text-white text-3xl z-[101] drop-shadow-md transition-transform duration-300 ${isMenuOpen ? 'rotate-90' : 'rotate-0'}`}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        {isMenuOpen ? '✕' : '☰'}
      </div>

      {/* Backdrop overlay - click to close */}
      <div 
        className={`absolute inset-0 bg-black/50 z-[99] transition-opacity duration-300 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsMenuOpen(false)} 
      />

      {/* Animated Menu Panel */}
      <div 
        ref={menuRef} 
        className={`absolute top-0 right-0 bottom-0 w-[300px] bg-[#141414]/95 pt-20 px-5 pb-5 z-[100] flex flex-col gap-2.5 backdrop-blur-[10px] border-l border-white/10 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-transform ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <h2 className="text-white mb-5 text-lg uppercase opacity-70 tracking-[2px]">
          Camera Views
        </h2>
        {waypoints.map((waypoint, i) => {
          const zoneName = waypoint.camera?.anchor?.name || `View ${i + 1}`
          const isActive = i === currentZoneIndex
          return (
            <button 
              key={i} 
              onClick={() => goTo(i)} 
              className={`
                group
                w-full text-left cursor-pointer text-white p-[15px] text-base rounded-lg border transition-all duration-200
                hover:bg-white/20 hover:translate-x-[5px]
                ${isActive 
                  ? 'bg-white/15 border-white/10 border-l-white/50 border-l-[3px] translate-x-[5px]' 
                  : 'bg-transparent border-white/10 border-l-white/10 border-l translate-x-0'
                }
              `}
            >
              {zoneName}
            </button>
          )
        })}
      </div>
    </>
  )
}

OrbitNavigation.propTypes = {
  xyzAPI: PropTypes.object
}
